import { useState, useMemo } from 'react';
import { Box, LoadingOverlay, ScrollArea, Stack, Text } from '@mantine/core';

import useDesktop from '@/hooks/useDesktop';

import type { RestaurantKey, VendorAvailabilityListItem } from '../types';

import RestaurantListSearch from './RestaurantList.search';
import RestaurantListItem from './RestaurantList.item';

type RestaurantListSectionProps = {
  restaurants: VendorAvailabilityListItem[];
  loading: boolean;
  selectedKey: RestaurantKey | null;
  onSelect: (key: RestaurantKey) => void;
};

const isKeyMatch = (key: RestaurantKey | null, item: VendorAvailabilityListItem): boolean =>
  key !== null && key.vendorId === item.vendorId && key.productType === item.productType;

const RestaurantListSection: React.FC<RestaurantListSectionProps> = ({
  restaurants,
  loading,
  selectedKey,
  onSelect,
}) => {
  const isDesktop = useDesktop();
  const [search, setSearch] = useState('');

  const filteredRestaurants = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? restaurants.filter((r) => r.internalName.toLowerCase().includes(query))
      : restaurants;

    return filtered;
  }, [restaurants, search]);

  const items = (
    <>
      {filteredRestaurants.length === 0 && !loading ? (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          No restaurants found
        </Text>
      ) : (
        <Stack gap={4}>
          {filteredRestaurants.map((item) => (
            <RestaurantListItem
              key={`${item.vendorId}-${item.productType}`}
              item={item}
              isSelected={isKeyMatch(selectedKey, item)}
              onSelect={() => onSelect({ vendorId: item.vendorId, productType: item.productType })}
            />
          ))}
        </Stack>
      )}
    </>
  );

  return (
    <Stack gap={0} pos="relative" h={isDesktop ? '100%' : undefined} bg="gray.1" px="xs" py="sm">
      <LoadingOverlay visible={loading} />

      <Box style={{ zIndex: 1 }} pos="sticky" top={0} mb="xs" px="xs">
        <Text size="xs" mb="xs">
          <Text span size="xs">
            Your restaurants{' '}
          </Text>
          <Text span fw={600} size="xs">
            ({filteredRestaurants.length})
          </Text>
        </Text>
        <RestaurantListSearch
          value={search}
          onChange={setSearch}
          totalCount={filteredRestaurants.length}
        />
      </Box>

      <ScrollArea
        type="auto"
        offsetScrollbars="present"
        flex={isDesktop ? 1 : undefined}
        h={isDesktop ? '100%' : '400px'}
      >
        {items}
      </ScrollArea>
    </Stack>
  );
};

export default RestaurantListSection;
